import { layoutRect, viewportHeight as layoutViewportHeight, viewportWidth as layoutViewportWidth } from "../../utils/displayDensity";
import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Info } from "lucide-react";
import "./xboxMethodologyPopover.css";

export default function XboxMethodologyPopover() {
  const id = useId();
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const timerRef = useRef(null);
  const pointerOpenRef = useRef(false);
  const hoveredRef = useRef(false);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState(null);
  const cancelClose = () => clearTimeout(timerRef.current);
  const show = () => { cancelClose(); setOpen(true); };
  const close = () => { cancelClose(); setOpen(false); };
  const scheduleClose = () => {
    cancelClose();
    timerRef.current = setTimeout(() => {
      const focused = document.activeElement;
      if (!hoveredRef.current && focused !== triggerRef.current && !panelRef.current?.contains(focused)) setOpen(false);
    }, 160);
  };

  useEffect(() => () => clearTimeout(timerRef.current), []);
  useLayoutEffect(() => {
    if (!open) { setPosition(null); return undefined; }
    const update = () => {
      const trigger = triggerRef.current;
      const panel = panelRef.current;
      if (!trigger || !panel || !layoutRect(trigger).width || trigger.closest("[inert]")) { setOpen(false); return; }
      const rect = layoutRect(trigger);
      const width = Math.min(1000, layoutViewportWidth() - 24);
      const height = Math.min(panel.scrollHeight, layoutViewportHeight() - 24);
      const below = rect.bottom + 10;
      const top = below + height <= layoutViewportHeight() - 12 ? below : Math.max(12, Math.min(rect.top - height - 10, layoutViewportHeight() - height - 12));
      setPosition({ left: Math.max(12, Math.min(rect.right - width, layoutViewportWidth() - width - 12)), top, width });
    };
    update();
    const onKey = (event) => { if (event.key === "Escape") close(); };
    const onOutside = (event) => {
      if (!triggerRef.current?.contains(event.target) && !panelRef.current?.contains(event.target)) close();
    };
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onOutside);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onOutside);
    };
  }, [open]);

  return <>
    <button ref={triggerRef} type="button" className="xbox-estimate-info xbox-methodology-trigger" aria-label="Xbox activity methodology" aria-haspopup="dialog" aria-expanded={open} aria-controls={open ? id : undefined}
      onPointerEnter={(event) => { if (event.pointerType === "mouse") { hoveredRef.current = true; show(); } }}
      onPointerLeave={(event) => { if (event.pointerType === "mouse") { hoveredRef.current = false; scheduleClose(); } }}
      onFocus={show} onBlur={scheduleClose}
      onKeyDown={(event) => {
        if (event.key === "Tab" && !event.shiftKey && open) { event.preventDefault(); panelRef.current?.focus({ preventScroll: true }); }
      }}
      onPointerDown={() => { pointerOpenRef.current = open; }}
      onClick={() => { cancelClose(); setOpen(!pointerOpenRef.current); }}>
      <Info size={14} aria-hidden="true" />
    </button>
    {open && createPortal(
      <section ref={panelRef} id={id} role="dialog" aria-modal="false" aria-labelledby={`${id}-title`} tabIndex={0} className="xbox-methodology-popover" style={{ ...position, visibility: position ? "visible" : "hidden" }}
        onMouseEnter={() => { hoveredRef.current = true; cancelClose(); }}
        onMouseLeave={() => { hoveredRef.current = false; scheduleClose(); }} onFocus={cancelClose} onBlur={scheduleClose}
        onKeyDown={(event) => {
          if (event.key === "Tab" && event.shiftKey && event.target === panelRef.current) { event.preventDefault(); triggerRef.current?.focus({ preventScroll: true }); }
        }}>
        <header>
          <h2 id={`${id}-title`}>XBOX ACTIVITY METHODOLOGY</h2>
          <p className="xbox-methodology-subtitle">Independent, panel-based measurement of Xbox-console activity.</p>
        </header>
        <div className="xbox-methodology-section">
          <h3>HOW WE MEASURE</h3>
          <p>mindGAME monitors a continuously expanding panel of millions of Xbox-network accounts (XUIDs), using Microsoft's Xbox Presence infrastructure to identify active games and applications.</p>
          <p>Each collection scans the panel for qualifying foreground activity on Xbox console devices:</p>
          <ul><li>Xbox Series X|S</li><li>Xbox One</li><li>Xbox 360</li></ul>
          <p>Windows PC, PlayStation, Nintendo, and mobile activity are excluded.</p>
          <p>Individual accounts are deduplicated within each title, allowing us to measure observed concurrent users and calculate each title's share of Xbox-console activity.</p>
        </div>
        <div className="xbox-methodology-section">
          <h3>VALIDATION &amp; RELIABILITY</h3>
          <p><strong>Large-scale observation.</strong> Millions of Xbox-network accounts are checked during each collection, producing hundreds of thousands of direct console-activity observations.</p>
          <p><strong>Independent sample consistency.</strong> In a validation test of 5.09 million XUIDs, the panel was randomly divided into five non-overlapping groups of approximately 1 million accounts each.</p>
          <p>All five groups produced the same top 10 Xbox games, with closely aligned Player Share measurements. For example, Gears of War: E-Day ranked #2 in every group, with Games-Only Share ranging from approximately 7.65% to 7.93%.</p>
          <p><strong>Consistent methodology.</strong> The same device restrictions, foreground-activity requirements, and title-level deduplication are applied across collections.</p>
          <p>These checks demonstrate strong internal measurement stability. They do not independently establish that the panel is a perfectly representative random sample of every Xbox-console user worldwide.</p>
        </div>
        <div className="xbox-methodology-section">
          <h3>HOW TO INTERPRET THE DATA</h3>
          <dl>
            <dt>Observed Users</dt><dd>The number of distinct accounts in the sampled panel reporting qualifying foreground activity for a title during the collection window.</dd>
            <dt>Player Share</dt><dd>A title's percentage of qualifying observed activity within the selected population (Games, Apps, or All Activity).</dd>
            <dt>Estimated Concurrent Players</dt><dd>A modeled estimate of the broader Xbox-console population playing or using a title, derived from observed Player Share and time-of-day population calibration.</dd>
            <dt>Rank</dt><dd>A title's position relative to other titles within the selected activity category.</dd>
          </dl>
          <p>A reported zero indicates no qualifying players observed in the sampled panel during that collection. It does not establish that no one worldwide is playing the title.</p>
        </div>
        <footer>Source: Xbox Presence data, independently collected and analyzed by mindGAME. Estimates are model-based and are not official Microsoft-reported player counts.</footer>
      </section>, document.body)}
  </>;
}